/**
 * Legal + banking requisites of the operator (продавец/исполнитель) behind
 * uztender.com — the browser-facing shape, reused by the оферта, privacy
 * policy, the /requisites page, the bank-transfer block on /plans and the
 * printable «Счёт на оплату» (/schet).
 *
 * ⚠️ The DATA now lives in `@tenders/shared` (`COMPANY_LEGAL`), because the API
 * renders a real payable PDF invoice server-side and cannot import from
 * `apps/web`. This module is the presentation layer over it — it derives the
 * display phone and the `mailto:`/`tel:` hrefs, and adds the two browser-only
 * channels (the public Telegram channel). Change a digit in shared, not here.
 * VERBATIM company data — do not paraphrase.
 */
import { COMPANY_LEGAL, COMPANY_PHONE_E164 } from '@tenders/shared';
import { countryConfig } from '../country';
import { formatPhone } from './format';

/** E.164, digits only — the `tel:` href and the source for the display form. */
const PHONE_E164 = COMPANY_PHONE_E164;

export const COMPANY = {
  legalName: COMPANY_LEGAL.legalName,
  shortName: COMPANY_LEGAL.shortName,
  address: COMPANY_LEGAL.address,
  account: COMPANY_LEGAL.account,
  bankName: COMPANY_LEGAL.bankName,
  mfo: COMPANY_LEGAL.mfo,
  inn: COMPANY_LEGAL.inn,
  oked: COMPANY_LEGAL.oked,
  vatReg: COMPANY_LEGAL.vatReg,
  // Contact channels (also required disclosure under the e-commerce law, ЗРУ-792).
  //
  // ⚠️ This one constant is the company's published contact everywhere: the
  // requisites card, /about, /plans, the printable invoice, the оферта and the
  // privacy policy all read it, and `catalog.tsx` puts it in the Organization
  // JSON-LD that Google and the AI crawlers ingest.
  //
  // The support address + website are the PER-COUNTRY brand contact
  // (countryConfig.brand): the KZ site publishes support@kaztender.com /
  // kaztender.com, the UZ site support@uztender.com / uztender.com — the SAME
  // legal entity (ООО FASTAGO, whose requisites below stay shared), a different
  // brand-facing contact. `COMPANY_LEGAL.email`/`.website` remain the UZ values
  // the server-side invoice still prints.
  email: countryConfig.brand.supportEmail,
  emailUrl: `mailto:${countryConfig.brand.supportEmail}`,
  // Public sales/support line. The display form is DERIVED via the same
  // formatPhone() the tender pages use, so our own number never renders in a
  // different grouping than the buyer phones next to it. The href keeps the raw
  // E.164 value — a spaced/dashed number breaks mobile dialers.
  phone: formatPhone(PHONE_E164),
  phoneUrl: `tel:${PHONE_E164}`,
  // Telegram support contact = the operator's handle @flesteyto. Since 2026-09-20
  // this is the ONE support identity: `COMPANY_LEGAL.telegram` in shared carries
  // the same handle, so the site, the requisites card and any server-rendered
  // document name the same account. UZ-only: a telegram-off deployment (KZ,
  // messaging.telegram === false) must NOT render it — every render site is
  // gated on HAS_TELEGRAM.
  telegram: '@flesteyto',
  telegramUrl: 'https://t.me/flesteyto',
  // The Telegram contact this DEPLOYMENT publishes as a way to reach a person —
  // `countryConfig.brand.supportTelegram`, or null for «show none». On UZ it is
  // the same handle as `telegram` above. It is a separate pair because of what
  // gates it: `telegram`/`telegramUrl` render behind HAS_TELEGRAM (the bot
  // deployment — sales prompts, churn, checkout help), this one behind
  // HAS_SUPPORT_TELEGRAM (the contact surfaces: requisites, support modal, /about).
  supportTelegram: countryConfig.brand.supportTelegram?.handle ?? '',
  supportTelegramUrl: countryConfig.brand.supportTelegram?.url ?? '',
  // WhatsApp — the operator's line as a wa.me link (digits only, per the wa.me
  // spec). Shown in the requisites card (it replaced the Telegram row) and works
  // on both deployments, so it is NOT gated on messaging.telegram.
  whatsappUrl: 'https://wa.me/998901877798',
  // Public content channel (auto-posted large/notable tenders) — distinct from
  // `telegram`/`telegramUrl` above, which is the support/contact account. From
  // the country config: @uztender_uz on UZ; '' on KZ, which has a bot but no
  // channel — every render site is gated on HAS_TELEGRAM_CHANNEL.
  channel: countryConfig.messaging.telegramChannel?.handle ?? '',
  channelUrl: countryConfig.messaging.telegramChannel?.url ?? '',
  website: countryConfig.brand.siteUrl,
} as const;

/** Whether this deployment runs the Telegram BOT — and with it every sales /
 *  connect link that assumes one. Both deployments since 2026-10-14 (KZ:
 *  @kaztender_com_bot). */
export const HAS_TELEGRAM = countryConfig.messaging.telegram;

/** Whether this deployment has a public Telegram CHANNEL to advertise. UZ yes
 *  (@uztender_uz); KZ no — it has a bot and no channel. */
export const HAS_TELEGRAM_CHANNEL = countryConfig.messaging.telegramChannel != null;

/**
 * Whether a human Telegram CONTACT is published (requisites card, the «Помощь и
 * поддержка» modal, /about). Independent of the bot: Kazakhstan has no bot and
 * still lists Telegram + email as its two contacts (owner, 2026-10-13).
 */
export const HAS_SUPPORT_TELEGRAM = countryConfig.brand.supportTelegram != null;

/**
 * Whether the operator's phone — and WhatsApp, which is the same number — is
 * published. Off on Kazakhstan (owner, 2026-10-13: it is his own Uzbek mobile).
 * The legal requisites above it in the card are unaffected.
 */
export const PUBLISH_PHONE = countryConfig.brand.publishPhone;

/**
 * Ordered requisites rows for the requisites card / invoice. `label` is a plain
 * Russian caption (these are legal/banking terms with no common Uzbek variant);
 * `value` comes from COMPANY.
 */
export const COMPANY_ROWS: ReadonlyArray<{ label: string; value: string; href?: string }> = [
  { label: 'Полное наименование', value: COMPANY.legalName },
  { label: 'Юридический адрес', value: COMPANY.address },
  { label: 'ИНН', value: COMPANY.inn },
  { label: 'Расчётный счёт (Р/С)', value: COMPANY.account },
  { label: 'Банк', value: COMPANY.bankName },
  { label: 'МФО', value: COMPANY.mfo },
  { label: 'ОКЭД', value: COMPANY.oked },
  { label: 'Рег. номер плательщика НДС (РКПНДС)', value: COMPANY.vatReg },
  ...(PUBLISH_PHONE ? [{ label: 'Телефон', value: COMPANY.phone, href: COMPANY.phoneUrl }] : []),
  { label: 'Email', value: COMPANY.email, href: COMPANY.emailUrl },
  // WhatsApp is the phone number as a wa.me link, so it follows the phone: both
  // or neither. (It was shown on both deployments until 2026-10-13.)
  ...(PUBLISH_PHONE
    ? [{ label: 'WhatsApp', value: COMPANY.phone, href: COMPANY.whatsappUrl }]
    : []),
  // Telegram, beside WhatsApp rather than instead of it: the privacy policy and
  // the оферта both send the reader here («обращайтесь по реквизитам»), and the
  // support account is the channel most of them actually use. Gated on the
  // published CONTACT, not on the bot — Kazakhstan has no bot and keeps this row.
  ...(HAS_SUPPORT_TELEGRAM
    ? [{ label: 'Telegram', value: COMPANY.supportTelegram, href: COMPANY.supportTelegramUrl }]
    : []),
];
